import { showConfirm } from './ConfirmDialog';
import { useState, useEffect, useRef } from 'react';
import { useDenseTableLabels } from '../hooks/useDenseTableLabels';
import { useTranslation } from 'react-i18next';
import { apiGetVenueFiles, apiUploadVenueFile, apiDeleteVenueFile, apiSetVenuePhoto, apiSetVenueFileCaption, type VenueFileDTO } from '../api';
import { useWriteGate } from '../lib/writeGate';

/**
 * VenueFilesSection — file store scoped to a VENUE (shared catalog). The
 * venue's own tech sheets / plans / photos are the same for every band that
 * plays there. Uploads any file (base64 → POST /api/venues/:id/files), lists
 * them with a download link, and lets editors delete. (2026-08-12)
 */
export default function VenueFilesSection({ venueId }: { venueId: string }) {
  const denseRef = useRef<HTMLTableElement | null>(null);
  useDenseTableLabels(denseRef);
  const { t } = useTranslation();
  // Capability-aware write gate (lot A2). `POST|DELETE /api/venues/:id/files` are
  // mounted AFTER the server's global read-only guard (server/index.js:585) →
  // 403 for a `users.role = 'read-only'` account. The venue catalog is SHARED
  // across bands, so there is no entity band to pass: the gate falls back to the
  // active band (a read-only account has no write there either way, and an
  // editor/superadmin keeps its write).
  const writeGate = useWriteGate();
  const canWrite = writeGate.canWrite;
  const [files, setFiles] = useState<VenueFileDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setFiles(await apiGetVenueFiles(venueId));
    } catch (e: any) {
      setError(e?.message || t('files.failedLoad'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, [venueId]);

  const onPick = async (file: File) => {
    if (!canWrite) return; // frozen for a global read-only account (server 403s)
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
        reader.onerror = () => reject(new Error(t('files.couldNotRead')));
        reader.readAsDataURL(file);
      });
      await apiUploadVenueFile(venueId, { filename: file.name, mime: file.type, base64 });
      await load();
    } catch (e: any) {
      setError(e?.message || t('files.uploadFailed'));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  // Photo « document » (2026-11-03) : une seule par salle, imprimée pleine largeur
  // dans la section venue-photo de la day sheet.
  const togglePhoto = async (f: VenueFileDTO) => {
    setError(null);
    try {
      await apiSetVenuePhoto(venueId, f.id, f.category !== 'photo');
      await load();
    } catch (e: any) {
      setError(e?.message || t('files.photoFailed'));
    }
  };

  // Légende libre de la photo (2026-11-03) : vide ⇒ le nom de la salle s'imprime.
  const saveCaption = async (f: VenueFileDTO, value: string) => {
    const caption = value.trim();
    if (caption === (f.caption || '')) return; // rien à faire
    setError(null);
    try {
      await apiSetVenueFileCaption(venueId, f.id, caption);
      await load();
    } catch (e: any) {
      setError(e?.message || t('files.captionFailed'));
    }
  };

  const remove = async (f: VenueFileDTO) => {
    if (!canWrite) return; // before the confirm dialog AND the API call
    if (!(await showConfirm(t('files.deleteConfirm', { filename: f.filename })))) return;
    try {
      await apiDeleteVenueFile(venueId, f.id);
      setFiles((prev) => prev.filter((x) => x.id !== f.id));
    } catch (e: any) {
      setError(e?.message || t('files.deleteFailed'));
    }
  };

  const fmtSize = (n: number) => {
    if (!n) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <input
          ref={inputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void onPick(f); }}
        />
        <button className="btn btn-primary" onClick={() => inputRef.current?.click()} disabled={uploading || !canWrite}>
          {uploading ? t('files.uploading') : t('files.uploadFile')}
        </button>
        <span className="text-dim" style={{ fontSize: 'var(--font-xs)' }}>
          {t('files.venueUploadHint')}{t('files.anyFile')}{t('files.sharedAcrossBands')}
        </span>
      </div>
      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
      {loading && <p className="text-dim">{t('files.loadingFiles')}</p>}
      {!loading && files.length === 0 && (
        <p className="text-dim">{t('files.noVenueFiles')}</p>
      )}
      {files.length > 0 && (
        <table ref={denseRef} className="table tbl-dense" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>{t('files.colFile')}</th>
              <th>{t('files.colSize')}</th>
              <th>{t('files.colUploaded')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {files.map((f) => (
              <tr key={f.id}>
                <td>
                  <a href={f.url} target="_blank" rel="noreferrer" download={f.filename}>{f.filename}</a>
                  {f.category === 'photo' && (
                    <span className="rc-chip" data-testid="venue-photo-badge" style={{ marginLeft: 8 }} title={t('files.photoHint')}>
                      {t('files.photoBadge')}
                    </span>
                  )}
                  {f.category === 'photo' && (
                    <div style={{ marginTop: 6 }}>
                      <input
                        className="form-input"
                        style={{ maxWidth: 320 }}
                        defaultValue={f.caption || ''}
                        maxLength={120}
                        placeholder={t('files.captionPh')}
                        onBlur={(e) => void saveCaption(f, e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void saveCaption(f, (e.target as HTMLInputElement).value); } }}
                        data-testid={`venue-photo-caption-${f.id}`}
                      />
                    </div>
                  )}
                </td>
                <td>{fmtSize(f.size)}</td>
                <td className="text-dim">{f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString() : ''}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {/^image\//i.test(f.mime || '') && (
                    <button
                      className="btn btn-secondary btn-small"
                      style={{ marginRight: 6 }}
                      onClick={() => void togglePhoto(f)}
                      disabled={!canWrite}
                      data-testid={`venue-photo-toggle-${f.id}`}
                    >
                      {f.category === 'photo' ? t('files.photoUnset') : t('files.photoSet')}
                    </button>
                  )}
                  <button className="btn btn-danger btn-small" onClick={() => void remove(f)} disabled={!canWrite}>{t('files.delete')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
